import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { winesLoad } from "../redux/winesLoad";
import { updateCart, setPageWines } from "../redux/winesSlice";
import WineShopHeader from "./WineShopHeader";
import PagesRouter from "../routes/PagesRouter";
import WineItem from "./WineItem";

function WineShop() {

  const location = useLocation();
  const dispatch = useDispatch();

  const wines = useSelector(
    state => state.wines
  );

  const [initialWinesArr, setInitialWinesArr] = useState(null);
  const [winesArr, setWinesArr] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [winesPerPage] = useState(12);
  const [isShowPages, setIsShowPages] = useState(false);
  const [selectedWine, setSelectedWine] = useState(null);


  /* =====================================================
     LOAD WINES
     ===================================================== */

  if (wines.dataLoadState === 0) {
    dispatch(winesLoad);
  }


  /* =====================================================
     LOAD CART FROM LOCAL STORAGE
     ===================================================== */

  useEffect(() => {

    const savedCart =
      localStorage.getItem("nevinnyShopCartData");

    if (savedCart) {

      try {

        const cartFromLocalStorage =
          JSON.parse(savedCart);

        dispatch(
          updateCart(cartFromLocalStorage)
        );

      } catch (error) {

        console.error(
          "Unable to load cart from localStorage:",
          error
        );

      }
    }

  }, [dispatch]);


  /* =====================================================
     SAVE CART BEFORE PAGE RELOAD
     ===================================================== */

  useEffect(() => {

    const saveCartToLocalStorage = () => {

      localStorage.setItem(
        "nevinnyShopCartData",
        JSON.stringify(wines.cart)
      );

    };

    window.addEventListener(
      "beforeunload",
      saveCartToLocalStorage
    );

    return () => {

      window.removeEventListener(
        "beforeunload",
        saveCartToLocalStorage
      );

    };

  }, [wines.cart]);


  /* =====================================================
     LOAD PAGE WINES

     This logic is directly inside useEffect so that
     react-hooks/exhaustive-deps warning is avoided.
     ===================================================== */

  useEffect(() => {

    if (wines.dataLoadState !== 2) {
      return;
    }

    if (!wines.initialData) {
      return;
    }

    switch (location.pathname) {

      /* ================= ITALY ================= */

      case "/italy": {

        const italyWines =
          wines.initialData.filter(
            v =>
              v.region &&
              v.region[0] &&
              v.region[0].toLowerCase() === "италия"
          );

        dispatch(
          setPageWines(italyWines)
        );

        break;
      }


      /* ================= SPAIN ================= */

      case "/spain": {

        const spainWines =
          wines.initialData.filter(
            v =>
              v.region &&
              v.region[0] &&
              v.region[0].toLowerCase() === "испания"
          );

        dispatch(
          setPageWines(spainWines)
        );

        break;
      }


      /* ================= FRANCE ================= */

      case "/france": {

        const franceWines =
          wines.initialData.filter(
            v =>
              v.region &&
              v.region[0] &&
              v.region[0].toLowerCase() === "франция"
          );

        dispatch(
          setPageWines(franceWines)
        );

        break;
      }


      /* ================= NEW WORLD ================= */

      case "/new-world": {

        const countries = [
          "аpгентина",
          "австралия",
          "чили",
          "новая зеландия",
          "юар",
          "сша"
        ];

        const newWorldWines = [];

        for (
          let i = 0;
          i < wines.initialData.length;
          i++
        ) {

          if (
            !wines.initialData[i].region ||
            !wines.initialData[i].region[0]
          ) {
            continue;
          }

          const region =
            wines.initialData[i]
              .region[0]
              .toLowerCase();

          for (
            let j = 0;
            j < countries.length;
            j++
          ) {

            if (
              region === countries[j]
            ) {

              newWorldWines.push(
                wines.initialData[i]
              );

              break;
            }

          }
        }

        dispatch(
          setPageWines(newWorldWines)
        );

        break;
      }


      /* ================= ALL WINES ================= */

      case "/shop": {

        dispatch(
          setPageWines(
            wines.initialData
          )
        );

        break;
      }


      /* ================= OTHER PAGES ================= */

      default:
        break;
    }

  }, [
    location.pathname,
    wines.dataLoadState,
    wines.initialData,
    dispatch
  ]);


  /* =====================================================
     SET INITIAL WINES
     ===================================================== */

  useEffect(() => {

    if (wines.dataLoadState === 2) {

      setInitialWinesArr(
        wines.data
      );

      setWinesArr(
        wines.data
      );

    }

  }, [
    wines.dataLoadState,
    wines.data
  ]);


  /* =====================================================
     RESET PAGINATION WHEN LOCATION CHANGES
     ===================================================== */

  useEffect(() => {

    setCurrentPage(1);

  }, [
    location.pathname
  ]);


  /* =====================================================
     FILTER WINES
     ===================================================== */

  function changedFilter(filterText) {

    if (!initialWinesArr) {
      return;
    }

    setCurrentPage(1);

    switch (filterText) {

      /* ================= RED ================= */

      case "red":

        setWinesArr(
          initialWinesArr.filter(
            v =>
              v.type &&
              v.type.toLowerCase() === "red"
          )
        );

        break;


      /* ================= WHITE ================= */

      case "white":

        setWinesArr(
          initialWinesArr.filter(
            v =>
              v.type &&
              v.type.toLowerCase() === "white"
          )
        );

        break;


      /* ================= DRY ================= */

      case "dry":

        setWinesArr(
          initialWinesArr.filter(
            v =>
              v.sugar &&
              v.sugar.toLowerCase() === "dry"
          )
        );

        break;


      /* ================= SEMI DRY ================= */

      case "semi-dry":

        setWinesArr(
          initialWinesArr.filter(
            v =>
              v.sugar &&
              v.sugar.toLowerCase() === "semi-dry"
          )
        );

        break;


      /* ================= SEMI SWEET ================= */

      case "semi-sweet":

        setWinesArr(
          initialWinesArr.filter(
            v =>
              v.sugar &&
              v.sugar.toLowerCase() === "semi-sweet"
          )
        );

        break;


      /* ================= SWEET ================= */

      case "sweet":

        setWinesArr(
          initialWinesArr.filter(
            v =>
              v.sugar &&
              v.sugar.toLowerCase() === "sweet"
          )
        );

        break;


      /* ================= ALL ================= */

      case "all":

        setWinesArr(
          initialWinesArr
        );

        break;


      default:
        break;
    }
  }


  /* =====================================================
     SORT WINES
     ===================================================== */

  function changedSort(sortType) {

    if (!winesArr) {
      return;
    }

    const sortedArr = [
      ...winesArr
    ];

    switch (sortType) {

      /* ================= RATING HIGH TO LOW ================= */

      case "raiting-decrease":

        setWinesArr(
          sortedArr.sort(
            (a, b) =>
              b.raiting - a.raiting
          )
        );

        break;


      /* ================= RATING LOW TO HIGH ================= */

      case "raiting-increase":

        setWinesArr(
          sortedArr.sort(
            (a, b) =>
              a.raiting - b.raiting
          )
        );

        break;


      /* ================= PRICE HIGH TO LOW ================= */

      case "price-decrease":

        setWinesArr(
          sortedArr.sort(
            (a, b) =>
              b.price - a.price
          )
        );

        break;


      /* ================= PRICE LOW TO HIGH ================= */

      case "price-increase":

        setWinesArr(
          sortedArr.sort(
            (a, b) =>
              a.price - b.price
          )
        );

        break;


      /* ================= ALL ================= */

      case "all":

        setWinesArr(
          initialWinesArr
        );

        break;


      default:
        break;
    }
  }


  /* =====================================================
     CHANGE OUTPUT TYPE
     ===================================================== */

  function changeOutputType(state) {

    setIsShowPages(
      state
    );

  }


  /* =====================================================
     PAGINATION
     ===================================================== */

  function paginate(number) {

    setCurrentPage(
      number
    );

  }


  /* =====================================================
     WINE ITEMS
     ===================================================== */

  let wineItemCode = null;

  if (winesArr) {

    /* ================= PAGINATION ================= */

    if (isShowPages) {

      const lastWineIndex =
        currentPage * winesPerPage;

      const firstWineIndex =
        lastWineIndex - winesPerPage;

      const currentPageWines =
        winesArr.slice(
          firstWineIndex,
          lastWineIndex
        );

      wineItemCode =
        currentPageWines.map(
          v => (

            <WineItem
              key={v.id}
              item={v}
              onSelect={() =>
                setSelectedWine(v)
              }
            />

          )
        );

    }

    /* ================= SHOW ALL ================= */

    else {

      wineItemCode =
        winesArr.map(
          v => (

            <WineItem
              key={v.id}
              item={v}
              onSelect={() =>
                setSelectedWine(v)
              }
            />

          )
        );

    }
  }


  /* =====================================================
     HEADER CONTROL

     Header is hidden on:

     /
     /home
     /login
     /profile

     Header is shown on all other pages.
     ===================================================== */

  const showHeader =
    location.pathname !== "/" &&
    location.pathname !== "/home" &&
    location.pathname !== "/login" &&
    location.pathname !== "/profile";


  /* =====================================================
     SELECTED WINE
     ===================================================== */

  if (selectedWine) {

    // Selected wine logic can be added here.

  }


  /* =====================================================
     RETURN
     ===================================================== */

  return (
    <Fragment>

      {/* ================= HEADER ================= */}

      {showHeader && (
        <WineShopHeader />
      )}


      {/* ================= PAGE ROUTER ================= */}

      <PagesRouter
        cbChangedFilter={changedFilter}
        cbChangedSort={changedSort}
        cbChangeOutputType={changeOutputType}

        loadState={
          wines.dataLoadState
        }

        wineItemCode={
          wineItemCode
        }

        loadError={
          wines.dataLoadError
        }

        isShowPages={
          isShowPages
        }

        winesArrLength={
          winesArr
            ? winesArr.length
            : null
        }

        winesPerPage={
          winesPerPage
        }

        cbPaginate={
          paginate
        }

        currentPage={
          currentPage
        }
      />

    </Fragment>
  );
}

export default WineShop;